/** Shared effect-family cleanup. Removing an area also removes its conditions,
 * including descendants, without touching an unrelated cast or caster. */
export const removeEffectFamily = (effects = [], removedIds = []) => {
  const removed = new Set(removedIds);
  let changed = true;
  while (changed) {
    changed = false;
    for (const effect of effects) {
      if (effect.parentEffectId && removed.has(effect.parentEffectId) && !removed.has(effect.id)) {
        removed.add(effect.id);
        changed = true;
      }
    }
  }
  return effects.filter((effect) => !removed.has(effect.id));
};

const concentrationIdFor = (effect) => effect.concentrationId ??
  (effect.expiration?.type === 'concentration' ? effect.expiration.concentrationId : null);

/** A missing cast identity is never permission to end everybody's concentration. */
export const removeConcentrationEffects = (effects = [], concentrationId, sourceCombatantId) => {
  if (typeof concentrationId !== 'string' || !concentrationId.trim()) return effects;
  const roots = effects.filter((effect) => concentrationIdFor(effect) === concentrationId &&
    (!sourceCombatantId || effect.sourceCombatantId === sourceCombatantId));
  return removeEffectFamily(effects, roots.map((effect) => effect.id));
};

/** One active concentration spell per caster. Replay keeps the original root
 * and its children instead of deleting and re-creating a cast. */
export const replaceCasterConcentration = (effects = [], root) => {
  if (!root?.id || !root.sourceCombatantId || !concentrationIdFor(root)) {
    throw new Error('Concentration requires a stable effect, caster, and cast identity.');
  }
  const existing = effects.find((effect) => effect.id === root.id);
  if (existing) {
    if (existing.sourceCombatantId !== root.sourceCombatantId || concentrationIdFor(existing) !== concentrationIdFor(root)) {
      throw new Error('Effect identity belongs to another concentration cast.');
    }
    return effects;
  }
  const oldRoots = effects.filter((effect) => effect.sourceCombatantId === root.sourceCombatantId && concentrationIdFor(effect));
  return [...removeEffectFamily(effects, oldRoots.map((effect) => effect.id)), root];
};
